import React, { useEffect, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Modal,
  TextInput,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAssignmentStore } from '../../stores/assignmentStore';
import { AssignmentHistory } from '../../types';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { PartnershipView } from '../../components/assignment/PartnershipView';
import { useResponsiveStyles } from '../../hooks/useResponsiveStyles';
import { FeatureGuard } from '../../features/FeatureGuard';
import { useFeatureEnabled } from '../../features/FeatureFlagProvider';
import { CORE_FEATURES } from '../../features/types';
import theme from '../../theme';
import EditAssignmentModal from '../../components/assignment/EditAssignmentModal';
import ManageAssignmentModal from '../../components/assignment/ManageAssignmentModal';
import { updateAssignmentHistory, deleteAssignmentHistory } from '../../api/supabase';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useDriverStore } from '../../stores/driverStore';

export const AssignmentHistoryScreen: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<any, 'AssignmentHistoryList'>>();
  const { assignments, loading, error, fetchAssignments, completeAssignment, cancelAssignment } = useAssignmentStore();
  const { drivers } = useDriverStore();
  
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
  const [completedBy, setCompletedBy] = useState('');
  const [componentError, setComponentError] = useState<Error | null>(null);
  const responsive = useResponsiveStyles();
  
  // Enhanced features with feature flags
  const [viewMode, setViewMode] = useState<'table' | 'timeline' | 'cards' | 'hybrid'>('hybrid');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [exportModalVisible, setExportModalVisible] = useState(false);
  
  // Data issues modal state
  const [showIssuesModal, setShowIssuesModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);

  // Hybrid view state
  const [expandedAssignments, setExpandedAssignments] = useState<Set<string>>(new Set());
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [selectedFormType, setSelectedFormType] = useState<'receiving' | 'delivery'>('receiving');
  const [selectedAssignment, setSelectedAssignment] = useState<AssignmentHistory | null>(null);

  useEffect(() => {
    fetchAssignments();
  }, []);

  // Refresh assignments when screen comes into focus (e.g., after creating a new assignment)
  useFocusEffect(
    React.useCallback(() => {
      console.log('🔍 AssignmentHistoryScreen: Screen focused, refreshing assignments...');
      const refreshAndDebug = async () => {
        const previousCount = assignments.length;
        await fetchAssignments();
        console.log(`📊 Assignments after focus refresh: ${assignments.length} (was ${previousCount})`);
        
        if (assignments.length > previousCount) {
          console.log('✅ New assignments detected after refresh!');
        } else if (assignments.length === previousCount) {
          console.log('⚠️ No new assignments found after refresh');
        }
      };
      refreshAndDebug();
    }, [fetchAssignments, assignments.length])
  );

  // Listen for store refresh events from assignment creation
  useEffect(() => {
    const handleStoreRefresh = () => {
      console.log('🔄 AssignmentHistoryScreen: Received store refresh event, refetching...');
      fetchAssignments();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('refreshStores', handleStoreRefresh);
      return () => window.removeEventListener('refreshStores', handleStoreRefresh);
    }
  }, [fetchAssignments]);

  // Error boundary fallback
  if (componentError) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: 'red', marginBottom: 10 }}>
          Assignment History Error
        </Text>
        <Text style={{ textAlign: 'center', marginBottom: 20 }}>
          {componentError?.message || 'An error occurred'}
        </Text>
        <TouchableOpacity 
          style={{ backgroundColor: '#007bff', padding: 10, borderRadius: 5 }}
          onPress={() => setComponentError(null)}
        >
          <Text style={{ color: 'white' }}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  useEffect(() => {
    const fetchAndDebug = async () => {
      console.log('🔍 AssignmentHistoryScreen: Fetching assignments...');
      await fetchAssignments();
      
      // Get fresh assignments from store after fetch
      const currentAssignments = useAssignmentStore.getState().assignments;
      
      // Debug: Log assignment data for partner assignment troubleshooting
      console.log('📊 Total assignments fetched:', currentAssignments.length);
      console.log('🤝 Partner assignments:', currentAssignments.filter(a => a.is_shared).length);
      console.log('👥 Assignments with secondary drivers:', currentAssignments.filter(a => a.secondary_driver_id).length);
      
      // Log recent assignments for debugging
      if (currentAssignments.length > 0) {
        console.log('📋 Recent assignments:');
        currentAssignments.slice(0, 3).forEach((assignment, index) => {
          console.log(`  ${index + 1}. ${assignment.vehicle?.license_plate || 'Unknown'}`);
          console.log(`     Primary: ${assignment.primaryDriver?.name || 'Unknown'}`);
          console.log(`     Secondary: ${assignment.secondaryDriver?.name || 'None'}`);
          console.log(`     Shared: ${assignment.is_shared ? 'Yes' : 'No'}`);
          console.log(`     Status: ${assignment.status}`);
          console.log('');
        });
      }
    };
    
    fetchAndDebug();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAssignments();
    setRefreshing(false);
  };

  // Define table columns for ResponsiveTable
  const tableColumns = [
    {
      key: 'vehicle',
      title: 'Vehicle Number',
      render: (value: any, item: AssignmentHistory) => (
        <Text style={styles.vehicleText}>
          {item.vehicle?.license_plate || 'Unknown Vehicle'}
        </Text>
      ),
    },
    {
      key: 'vehicle_make_model',
      title: 'Vehicle Make & Model',
      render: (value: any, item: AssignmentHistory) => (
        <Text style={styles.vehicleText}>
          {item.vehicle?.make_model || 'N/A'}
        </Text>
      ),
    },
    {
      key: 'driver',
      title: 'Driver',
      render: (value: any, item: AssignmentHistory) => (
        <Text style={styles.driverText}>
          {item.primaryDriver?.name || 'Unknown Driver'}
        </Text>
      ),
    },
    {
      key: 'phone',
      title: 'Phone',
      render: (value: any, item: AssignmentHistory) => (
        <Text style={styles.phoneText}>
          {item.primaryDriver?.phone || 'N/A'}
        </Text>
      ),
    },
    {
      key: 'photos',
      title: '📷',
      render: (value: any, item: AssignmentHistory) => {
        const deliveryCount = item.delivery_photos?.length || 0;
        const receivingCount = item.receiving_photos?.length || 0;
        const totalPhotos = deliveryCount + receivingCount;
        
        return totalPhotos > 0 ? (
          <View style={styles.photoIndicator}>
            <Ionicons name="images" size={16} color={theme.colors.info} />
            <Text style={styles.photoCountBadge}>{totalPhotos}</Text>
          </View>
        ) : (
          <Ionicons name="images-outline" size={16} color={theme.colors.gray300} />
        );
      },
    },
    {
      key: 'assigned_at',
      title: 'Date',
      render: (value: any, item: AssignmentHistory) => (
        <Text style={styles.dateText}>
          {new Date(item.assignment_date).toLocaleDateString()}
        </Text>
      ),
    },
    {
      key: 'rate',
      title: 'Share',
      render: (value: any, item: AssignmentHistory) => (
        <Text style={styles.rateText}>
          AED {item.monthly_rent_rate || item.daily_rate || 0}
          {item.monthly_rent_rate ? '/month' : '/day'}
        </Text>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value: any, item: AssignmentHistory) => (
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
          <Text style={styles.statusText}>{getStatusDisplayText(item)}</Text>
        </View>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (value: any, item: AssignmentHistory) => (
        <View style={styles.actionsContainer}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => handleRowClick(item)}
          >
            <Ionicons name="document-text-outline" size={16} color={theme.colors.primary} />
          </TouchableOpacity>
          {/* Removed old modal-based completion button - using new compact buttons instead */}
        </View>
      ),
    },
  ];

  // Enhanced filtering and search with feature flags
  const filteredAndSearchedAssignments = React.useMemo(() => {
    let result = assignments;
    
    // Apply status filter
    result = result.filter(assignment => {
      if (statusFilter === 'all') return true;
      
      // Smart status filtering based on end date logic
      if (statusFilter === 'active') {
        return !assignment.return_date && assignment.status === 'active';
      }
      
      if (statusFilter === 'completed') {
        return assignment.return_date || assignment.status === 'completed';
      }
      
      return assignment.status === statusFilter;
    });
    
    // Apply search filter (enhanced feature)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(assignment => 
        assignment.vehicle?.license_plate?.toLowerCase().includes(query) ||
        assignment.vehicle?.make_model?.toLowerCase().includes(query) ||
        assignment.primaryDriver?.name?.toLowerCase().includes(query) ||
        assignment.primaryDriver?.phone?.includes(query) ||
        assignment.secondaryDriver?.name?.toLowerCase().includes(query) ||
        assignment.secondaryDriver?.phone?.includes(query) ||
        assignment.id.toLowerCase().includes(query)
      );
    }
    
    // Apply date range filter (enhanced feature)
    if ((dateRange.start || dateRange.end)) {
      result = result.filter(assignment => {
        const assignmentDate = new Date(assignment.assignment_date);
        if (dateRange.start && assignmentDate < dateRange.start) return false;
        if (dateRange.end && assignmentDate > dateRange.end) return false;
        return true;
      });
    }
    
    // Sort by assignment date, newest first
    return result.sort((a, b) => {
      return new Date(b.assignment_date).getTime() - new Date(a.assignment_date).getTime();
    });
  }, [assignments, statusFilter, searchQuery, dateRange]);

  // Performance metrics for enhanced features
  const performanceMetrics = React.useMemo(() => {
    const completedAssignments = assignments.filter(a => a.return_date && a.status === 'completed');
    const totalRevenue = completedAssignments.reduce((sum, a) => sum + (a.daily_rate || 0), 0);
    const avgDuration = completedAssignments.length > 0 
      ? completedAssignments.reduce((sum, a) => {
          const start = new Date(a.assignment_date).getTime();
          const end = new Date(a.return_date!).getTime();
          return sum + (end - start) / (1000 * 60 * 60 * 24); // days
        }, 0) / completedAssignments.length
      : 0;
    
    return {
      totalAssignments: assignments.length,
      completedAssignments: completedAssignments.length,
      totalRevenue,
      avgDuration: Math.round(avgDuration),
      utilizationRate: assignments.length > 0 ? (completedAssignments.length / assignments.length) * 100 : 0
    };
  }, [assignments]);

  // Export functionality
  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    Alert.alert(
      'Export Assignment History',
      `Export ${filteredAndSearchedAssignments.length} assignments as ${format.toUpperCase()}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Export',
          onPress: () => {
            // TODO: Implement actual export functionality
            console.log(`Exporting ${filteredAndSearchedAssignments.length} assignments as ${format}`);
            Alert.alert('Export Started', `Your ${format.toUpperCase()} file will be ready shortly.`);
            setExportModalVisible(false);
          }
        }
      ]
    );
  };

  // Utility functions for status handling
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#f59e0b';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return 'time-outline';
      case 'completed': return 'checkmark-circle-outline';
      case 'cancelled': return 'close-circle-outline';
      default: return 'help-circle-outline';
    }
  };

  // Smart status display based on end date logic
  const getStatusDisplayText = (item: AssignmentHistory) => {
    // If assignment has no end date (return_date), it's in progress
    if (!item.return_date && item.status === 'active') {
      return 'IN PROGRESS';
    }
    
    // If assignment has end date but status is still active, mark as completed
    if (item.return_date && item.status === 'active') {
      return 'COMPLETED';
    }
    
    // Use original status for other cases
    switch (item.status) {
      case 'completed': return 'COMPLETED';
      case 'cancelled': return 'CANCELLED';
      default: return item.status.toUpperCase();
    }
  };

  // Timeline view component
  const renderTimelineView = () => (
    <ScrollView style={styles.timelineContainer}>
      {filteredAndSearchedAssignments.map((assignment, index) => (
        <View key={assignment.id} style={styles.timelineItem}>
          <View style={styles.timelineDot} />
          <View style={styles.timelineContent}>
            <Text style={styles.timelineDate}>
              {new Date(assignment.assignment_date).toLocaleDateString()}
            </Text>
            <Text style={styles.timelineTitle}>
              {assignment.vehicle?.license_plate} → {assignment.primaryDriver?.name}
              {assignment.is_shared && assignment.secondaryDriver && (
                <Text style={styles.partnerIndicator}> & {assignment.secondaryDriver.name}</Text>
              )}
            </Text>
            <Text style={styles.timelineSubtitle}>
              {assignment.vehicle?.make_model} • AED {assignment.daily_rate}/day
            </Text>
            <View style={[styles.timelineStatus, { backgroundColor: getStatusColor(assignment.status) }]}>
              <Text style={styles.timelineStatusText}>{getStatusDisplayText(assignment)}</Text>
            </View>
          </View>
        </View>
      ))}
    </ScrollView>
  );

  // Hybrid view functions
  const toggleExpanded = (assignmentId: string) => {
    const newExpanded = new Set(expandedAssignments);
    if (newExpanded.has(assignmentId)) {
      newExpanded.delete(assignmentId);
    } else {
      newExpanded.add(assignmentId);
    }
    setExpandedAssignments(newExpanded);
  };

  const handleViewForm = (assignment: AssignmentHistory, formType: 'receiving' | 'delivery') => {
    console.log('🔍 handleViewForm called - navigating to AssignmentDetails:', { 
      assignmentId: assignment.id, 
      formType, 
      vehiclePlate: assignment.vehicle?.license_plate 
    });
    
    // Navigate to the existing AssignmentDetailsScreen which uses ReceivingHistoryDisplay
    navigation.navigate('AssignmentDetails', {
      assignmentId: assignment.id
    });
  };

  const handleManageAssignment = (assignment: AssignmentHistory) => {
    console.log('🔧 Navigate to Manage Assignment:', assignment.id);
    navigation.navigate('ManageAssignment', { assignment });
  };

  // Hybrid view component
  const renderHybridView = () => (
    <ScrollView style={styles.hybridContainer}>
      {filteredAndSearchedAssignments.map((assignment) => (
        <PartnershipView
          key={assignment.id}
          assignment={assignment}
          isExpanded={expandedAssignments.has(assignment.id)}
          onToggleExpanded={() => toggleExpanded(assignment.id)}
          onViewForm={(formType) => handleViewForm(assignment, formType)}
          onManageAssignment={() => handleManageAssignment(assignment)}
        />
      ))}
    </ScrollView>
  );

  // Enhanced header with search and filters
  const renderEnhancedHeader = () => (
    <View style={styles.enhancedHeader}>
      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color={theme.colors.gray500} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search assignments, vehicles, drivers..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor={theme.colors.gray400}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color={theme.colors.gray500} />
          </TouchableOpacity>
        )}
      </View>

      {/* View Mode Toggle */}
      <View style={styles.viewModeContainer}>
        <View style={styles.viewModeToggle}>
          {(['table', 'timeline', 'cards'] as const).map((mode) => (
            <TouchableOpacity
              key={mode}
              style={[
                styles.viewModeButton,
                viewMode === mode && styles.activeViewModeButton
              ]}
              onPress={() => setViewMode(mode)}
            >
              <Ionicons 
                name={mode === 'table' ? 'list' : mode === 'timeline' ? 'git-commit' : 'grid'}
                size={16} 
                color={viewMode === mode ? theme.colors.white : theme.colors.gray600} 
              />
              <Text style={[
                styles.viewModeText,
                viewMode === mode && styles.activeViewModeText
              ]}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        
        <TouchableOpacity
          style={styles.exportButton}
          onPress={() => setExportModalVisible(true)}
        >
          <Ionicons name="download-outline" size={16} color={theme.colors.primary} />
          <Text style={styles.exportButtonText}>Export</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Smart filtering with improved status logic
  const filteredAssignments = filteredAndSearchedAssignments;

  // Calculate statistics
  const getStats = () => {
    const stats = {
      total: assignments.length,
      active: assignments.filter(a => a.status === 'active').length,
      completed: assignments.filter(a => a.status === 'completed').length,
      cancelled: assignments.filter(a => a.status === 'cancelled').length,
    };
    return stats;
  };

  const stats = getStats();

  // Event handlers
  const handleRowClick = async (assignment: AssignmentHistory) => {
    // Navigate to assignment details screen
    navigation.navigate('AssignmentDetails', { assignmentId: assignment.id });
  };

  // Old modal-based completion functions removed - using navigation-based completion instead

  const handleCompleteAssignment = (assignment: AssignmentHistory) => {
    // Navigate to Assignment screen in receiving mode with pre-filled assignment data
    console.log('Navigating to receiving form for assignment:', assignment.id);
    navigation.navigate('Assignment', {
      mode: 'receiving',
      assignmentData: {
        assignmentId: assignment.id,
        vehicleId: assignment.vehicle_id,
        driverId: assignment.primary_driver_id,
        secondaryDriverId: assignment.secondary_driver_id,
        isSharedAssignment: assignment.is_shared
      }
    });
  };

  const handleCompleteAssignmentModal = async () => {
    if (!selectedAssignmentId || !completedBy.trim()) {
      Alert.alert('Error', 'Please enter who completed the assignment');
      return;
    }

    try {
      console.log('Completing assignment:', selectedAssignmentId, 'by:', completedBy.trim());
      await completeAssignment(selectedAssignmentId, completedBy.trim());
      setCompleteModalVisible(false);
      setSelectedAssignmentId(null);
      setCompletedBy('');
      Alert.alert('Success', 'Assignment completed successfully');
      // Refresh the assignments list
      await fetchAssignments();
    } catch (error) {
      console.error('Complete assignment error:', error);
      Alert.alert('Error', 'Failed to complete assignment. Please try again.');
    }
  };

  // Show loading state if still loading and no data
  if (loading && assignments.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading assignment history...</Text>
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      {/* Enhanced Header */}
      {renderEnhancedHeader()}
      
      <ScrollView 
        style={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
          />
        }
      >
        {/* Statistics */}
        <View style={styles.statsContainer}>
          <View style={styles.statCard}>
            <Text style={styles.statNumber}>{stats.total}</Text>
            <Text style={styles.statLabel}>Total</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statNumber, { color: '#f59e0b' }]}>{stats.active}</Text>
            <Text style={styles.statLabel}>Active</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statNumber, { color: '#10b981' }]}>{stats.completed}</Text>
            <Text style={styles.statLabel}>Completed</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={[styles.statNumber, { color: '#ef4444' }]}>{stats.cancelled}</Text>
            <Text style={styles.statLabel}>Cancelled</Text>
          </View>
        </View>

      {/* View Mode Toggle */}
      <View style={styles.viewModeContainer}>
        <TouchableOpacity 
          style={[styles.viewModeButton, viewMode === 'hybrid' && styles.activeViewMode]}
          onPress={() => setViewMode('hybrid')}
        >
          <Ionicons name="layers-outline" size={20} color={viewMode === 'hybrid' ? '#FFFFFF' : '#6b7280'} />
          <Text style={[styles.viewModeText, viewMode === 'hybrid' && styles.activeViewModeText]}>Smart</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.viewModeButton, viewMode === 'table' && styles.activeViewMode]}
          onPress={() => setViewMode('table')}
        >
          <Ionicons name="list-outline" size={20} color={viewMode === 'table' ? '#FFFFFF' : '#6b7280'} />
          <Text style={[styles.viewModeText, viewMode === 'table' && styles.activeViewModeText]}>Table</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.viewModeButton, viewMode === 'timeline' && styles.activeViewMode]}
          onPress={() => setViewMode('timeline')}
        >
          <Ionicons name="time-outline" size={20} color={viewMode === 'timeline' ? '#FFFFFF' : '#6b7280'} />
          <Text style={[styles.viewModeText, viewMode === 'timeline' && styles.activeViewModeText]}>Timeline</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.viewModeButton, viewMode === 'cards' && styles.activeViewMode]}
          onPress={() => setViewMode('cards')}
        >
          <Ionicons name="grid-outline" size={20} color={viewMode === 'cards' ? '#FFFFFF' : '#6b7280'} />
          <Text style={[styles.viewModeText, viewMode === 'cards' && styles.activeViewModeText]}>Cards</Text>
        </TouchableOpacity>
      </View>

      {/* Content based on view mode */}
      {viewMode === 'hybrid' ? renderHybridView() : 
       viewMode === 'timeline' ? renderTimelineView() : (
        <ResponsiveTable
          data={filteredAssignments}
          columns={tableColumns}
          onRowPress={handleRowClick}
        />
      )}
      </ScrollView>

      {/* Complete Assignment Modal */}
      <Modal
        visible={completeModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setCompleteModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Complete Assignment</Text>
            <TextInput
              style={styles.modalInput}
              placeholder="Completed by (name)"
              value={completedBy}
              onChangeText={setCompletedBy}
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setCompleteModalVisible(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={handleCompleteAssignmentModal}
              >
                <Text style={styles.confirmButtonText}>Complete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Export Modal */}
      <Modal
        visible={exportModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setExportModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Export Assignment History</Text>
            <Text style={styles.modalSubtitle}>Choose export format:</Text>
            
            <TouchableOpacity 
              style={styles.exportOption}
              onPress={() => handleExport('csv')}
            >
              <Ionicons name="document-text-outline" size={24} color="#10b981" />
              <Text style={styles.exportOptionText}>CSV Format</Text>
              <Text style={styles.exportOptionSubtext}>Spreadsheet compatible</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.exportOption}
              onPress={() => handleExport('excel')}
            >
              <Ionicons name="grid-outline" size={24} color="#059669" />
              <Text style={styles.exportOptionText}>Excel Format</Text>
              <Text style={styles.exportOptionSubtext}>Advanced formatting</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.exportOption}
              onPress={() => handleExport('pdf')}
            >
              <Ionicons name="document-outline" size={24} color="#dc2626" />
              <Text style={styles.exportOptionText}>PDF Format</Text>
              <Text style={styles.exportOptionSubtext}>Print-ready document</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={() => setExportModalVisible(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );

  const handleCancelAssignment = (id: string) => {
    Alert.alert(
      'Cancel Assignment',
      'Are you sure you want to cancel this assignment?',
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Yes',
          style: 'destructive',
          onPress: async () => {
            try {
              await cancelAssignment(id);
              Alert.alert('Success', 'Assignment cancelled successfully');
              // Refresh the assignments list
              await fetchAssignments();
            } catch (error) {
              console.error('Cancel assignment error:', error);
              Alert.alert('Error', 'Failed to cancel assignment. Please try again.');
            }
          }
        }
      ]
    );
  };

  const renderAssignmentItem = ({ item }: { item: AssignmentHistory }) => (
    <TouchableOpacity style={styles.tableRow} onPress={() => handleRowClick(item)}>
      <View style={styles.tableCell}>
        <Text style={styles.tableCellText} numberOfLines={1}>
          {item.vehicle?.license_plate || `V${item.vehicle_id}`}
        </Text>
      </View>
      
      <View style={styles.tableCell}>
        <Text style={styles.tableCellText} numberOfLines={1}>
          {item.vehicle?.make_model || 'N/A'}
        </Text>
      </View>
      
      <View style={styles.tableCell}>
        <View>
          <Text style={styles.tableCellText} numberOfLines={1}>
            {item.primaryDriver?.name || 'Unknown'}
            {item.is_shared && <Text style={styles.primaryDriverIndicator}> (Primary)</Text>}
          </Text>
          {item.is_shared && item.secondaryDriver && (
            <Text style={[styles.tableCellText, styles.secondaryDriverText]} numberOfLines={1}>
              {item.secondaryDriver.name} (Partner)
            </Text>
          )}
        </View>
      </View>
      
      <View style={styles.tableCell}>
        <View>
          <Text style={styles.tableCellText} numberOfLines={1}>
            {item.primaryDriver?.phone || 'N/A'}
          </Text>
          {item.is_shared && item.secondaryDriver && (
            <Text style={[styles.tableCellText, styles.secondaryDriverText]} numberOfLines={1}>
              {item.secondaryDriver.phone || 'N/A'}
            </Text>
          )}
        </View>
      </View>
      
      <View style={styles.tableCell}>
        <Text style={styles.tableCellText} numberOfLines={1}>
          {new Date(item.assignment_date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
          })}
        </Text>
      </View>
      
      <View style={styles.tableCell}>
        <Text style={styles.tableCellText} numberOfLines={1}>
          {item.daily_rate}
        </Text>
      </View>
      
      <View style={styles.tableCell}>
        <View style={[styles.statusBadgeCompact, { backgroundColor: getStatusColor(item.status) }]}>
          <Text style={styles.statusTextCompact}>{item.status.toUpperCase()}</Text>
        </View>
      </View>
      
      <View style={styles.tableCell}>
        {item.status === 'active' ? (
          <View style={styles.actionRow}>
            <TouchableOpacity
              style={[styles.actionButtonCompact, { backgroundColor: 'green', borderWidth: 2, borderColor: 'white' }]}
              onPress={(e) => {
                e.stopPropagation();
                handleCompleteAssignment(item);
              }}
            >
              <Text style={[styles.actionButtonCompactText, { fontSize: 16, fontWeight: 'bold' }]}>✓</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButtonCompact, styles.cancelButtonCompact, { backgroundColor: 'red', borderWidth: 2, borderColor: 'white' }]}
              onPress={(e) => {
                e.stopPropagation();
                handleCancelAssignment(item.id);
              }}
            >
              <Text style={[styles.actionButtonCompactText, { fontSize: 16, fontWeight: 'bold' }]}>✕</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <Text style={styles.tableCellText}>-</Text>
        )}
      </View>
    </TouchableOpacity>
  );

  const renderFilterButtons = () => (
    <View style={styles.filterContainer}>
      {['all', 'active', 'completed', 'cancelled'].map((filter) => (
        <TouchableOpacity
          key={filter}
          style={[
            styles.filterButton,
            statusFilter === filter && styles.activeFilterButton
          ]}
          onPress={() => setStatusFilter(filter as any)}
        >
          <Text style={[
            styles.filterButtonText,
            statusFilter === filter && styles.activeFilterButtonText
          ]}>
            {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  // Data validation and issue detection with detailed information
  const dataIssues = React.useMemo(() => {
    const issues: Array<{
      id: string;
      type: 'missing_driver' | 'missing_vehicle' | 'status_mismatch' | 'duplicate' | 'missing_data';
      severity: 'high' | 'medium' | 'low';
      title: string;
      description: string;
      assignment: AssignmentHistory;
      actionable: boolean;
      suggestedFix?: string;
    }> = [];
    const duplicates = new Map<string, AssignmentHistory[]>();
    
    // Check for duplicates and logical issues
    assignments.forEach(assignment => {
      const key = `${assignment.vehicle_id}-${assignment.primary_driver_id}`;
      
      if (!duplicates.has(key)) {
        duplicates.set(key, []);
      }
      duplicates.get(key)!.push(assignment);
      
      // Check logical inconsistencies
      const hasEndDate = !!assignment.return_date;
      const isActiveStatus = assignment.status === 'active';
      
      // Issue: Active status but has end date
      if (isActiveStatus && hasEndDate) {
        issues.push({
          id: `status_mismatch_${assignment.id}`,
          type: 'status_mismatch',
          severity: 'high',
          title: 'Active Assignment with End Date',
          description: `Assignment for ${assignment.vehicle?.license_plate || 'Vehicle'} with ${assignment.primaryDriver?.name || 'Driver'} is marked as "Active" but has an end date (${assignment.return_date ? new Date(assignment.return_date).toLocaleDateString() : 'Unknown'}). This should be marked as "Completed".`,
          assignment,
          actionable: true,
          suggestedFix: 'Mark assignment as completed'
        });
      }
      
      // Issue: Completed status but no end date
      if (assignment.status === 'completed' && !hasEndDate) {
        issues.push({
          id: `missing_end_date_${assignment.id}`,
          type: 'status_mismatch',
          severity: 'high',
          title: 'Completed Assignment Missing End Date',
          description: `Assignment for ${assignment.vehicle?.license_plate || 'Vehicle'} with ${assignment.primaryDriver?.name || 'Driver'} is marked as "Completed" but has no end date. This makes tracking and billing calculations impossible.`,
          assignment,
          actionable: true,
          suggestedFix: 'Add end date or change status to active'
        });
      }
      
      // Issue: Missing critical vehicle data
      if (!assignment.vehicle?.license_plate) {
        issues.push({
          id: `missing_vehicle_${assignment.id}`,
          type: 'missing_vehicle',
          severity: 'high',
          title: 'Missing Vehicle Information',
          description: `Assignment ID: ${assignment.id} has no vehicle information. Vehicle ID: ${assignment.vehicle_id}. This prevents proper tracking and reporting. The vehicle may have been deleted or there's a data consistency issue.`,
          assignment,
          actionable: false,
          suggestedFix: 'Check if vehicle still exists in the vehicles table, or reassign to a valid vehicle'
        });
      }
      
      // Issue: Missing critical driver data  
      if (!assignment.primaryDriver?.name) {
        issues.push({
          id: `missing_driver_${assignment.id}`,
          type: 'missing_driver',
          severity: 'high',
          title: 'Missing Driver Information',
          description: `Assignment ID: ${assignment.id} for vehicle ${assignment.vehicle?.license_plate || assignment.vehicle_id} has no driver information. Driver ID: ${assignment.primary_driver_id}. This prevents proper tracking and billing. The driver may have been deleted or there's a data consistency issue.`,
          assignment,
          actionable: false,
          suggestedFix: 'Check if driver still exists in the drivers table, or reassign to a valid driver'
        });
      }
      
      // Issue: Missing assignment dates
      if (!assignment.assignment_date) {
        issues.push({
          id: `missing_start_date_${assignment.id}`,
          type: 'missing_data',
          severity: 'medium',
          title: 'Missing Assignment Start Date',
          description: `Assignment for ${assignment.vehicle?.license_plate || 'Vehicle'} with ${assignment.primaryDriver?.name || 'Driver'} has no start date. This prevents accurate billing calculations.`,
          assignment,
          actionable: true,
          suggestedFix: 'Set assignment start date'
        });
      }
    });
    
    // Check for overlapping assignments (same vehicle to different drivers)
    duplicates.forEach((assignmentList, key) => {
      if (assignmentList.length > 1) {
        const activeOverlaps = assignmentList.filter(a => !a.return_date);
        if (activeOverlaps.length > 1) {
          const [vehicleId, driverId] = key.split('-');
          const vehicle = activeOverlaps[0]?.vehicle?.license_plate || vehicleId;
          const driver = activeOverlaps[0]?.primaryDriver?.name || driverId;
          
          issues.push({
            id: `duplicate_${key}`,
            type: 'duplicate',
            severity: 'high',
            title: 'Duplicate Active Assignments',
            description: `Vehicle ${vehicle} has ${activeOverlaps.length} active assignments with driver ${driver}. This creates confusion in tracking and may cause billing issues. Assignment IDs: ${activeOverlaps.map(a => a.id).join(', ')}`,
            assignment: activeOverlaps[0],
            actionable: true,
            suggestedFix: 'End all but the most recent assignment'
          });
        }
      }
    });
    
    return issues;
  }, [assignments]);

  // Log data issues for debugging (can be removed in production)
  React.useEffect(() => {
    if (dataIssues.length > 0) {
      console.warn('🚨 Assignment Data Issues Detected:');
      dataIssues.forEach((issue, index) => {
        console.warn(`${index + 1}. ${issue}`);
      });
    }
  }, [dataIssues]);

  // Auto-fix functions with user approval
  const getFixSuggestions = (issue: any) => {
    if (!issue.actionable) return null;
    
    switch (issue.type) {
      case 'status_mismatch':
        if (issue.id.includes('status_mismatch')) {
          return {
            type: 'status_completion',
            description: 'Mark assignment as completed',
            action: () => handleAutoCompleteAssignment(issue.assignment.id),
            assignmentId: issue.assignment.id,
            assignment: issue.assignment
          };
        }
        if (issue.id.includes('missing_end_date')) {
          return {
            type: 'add_end_date',
            description: 'Add current date as end date',
            action: () => handleAddEndDate(issue.assignment.id),
            assignmentId: issue.assignment.id,
            assignment: issue.assignment
          };
        }
        break;
      case 'duplicate':
        return {
          type: 'resolve_duplicate',
          description: 'End older duplicate assignments',
          action: () => handleResolveDuplicate(issue.assignment.id),
          assignmentId: issue.assignment.id,
          assignment: issue.assignment
        };
      case 'missing_data':
        return {
          type: 'fix_data',
          description: 'Fix missing data',
          action: () => handleFixMissingData(issue.assignment.id),
          assignmentId: issue.assignment.id,
          assignment: issue.assignment
        };
    }
    
    return null;
  };

  const handleAutoCompleteAssignment = async (assignmentId: string) => {
    try {
      await completeAssignment(assignmentId, 'System Auto-Fix');
      Alert.alert('Success', 'Assignment marked as completed');
      await fetchAssignments(); // Refresh data
    } catch (error) {
      Alert.alert('Error', 'Failed to complete assignment');
    }
  };

  const handleAddEndDate = (assignmentId: string) => {
    Alert.alert(
      'Fix Assignment',
      'Choose how to fix this assignment:',
      [
        {
          text: 'Add current date as end date',
          onPress: async () => {
            try {
              // In a real implementation, you'd call an API to update return_date
              console.log('Adding current date as end date for:', assignmentId);
              Alert.alert('Success', 'End date added to assignment');
              await fetchAssignments();
            } catch (error) {
              Alert.alert('Error', 'Failed to update assignment');
            }
          }
        },
        {
          text: 'Revert to active status',
          onPress: async () => {
            try {
              // In a real implementation, you'd call an API to update status
              console.log('Reverting to active status for:', assignmentId);
              Alert.alert('Success', 'Assignment reverted to active');
              await fetchAssignments();
            } catch (error) {
              Alert.alert('Error', 'Failed to update assignment');
            }
          }
        },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const handleResolveDuplicate = (assignmentId: string) => {
    Alert.alert(
      'Resolve Duplicate Assignments',
      'This will end all older duplicate assignments and keep only the most recent one. Continue?',
      [
        {
          text: 'Yes, resolve',
          onPress: async () => {
            try {
              // In a real implementation, you'd find and end older assignments
              console.log('Resolving duplicate assignments for:', assignmentId);
              Alert.alert('Success', 'Duplicate assignments resolved');
              await fetchAssignments();
            } catch (error) {
              Alert.alert('Error', 'Failed to resolve duplicates');
            }
          }
        },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const handleFixMissingData = (assignmentId: string) => {
    Alert.alert(
      'Fix Missing Data',
      'This issue requires manual intervention. Please check the assignment details and update the missing information manually.',
      [
        {
          text: 'Open Assignment Details',
          onPress: () => {
            // Navigate to assignment details or show edit modal
            console.log('Opening assignment details for:', assignmentId);
          }
        },
        { text: 'OK', style: 'cancel' }
      ]
    );
  };

  const handleEditAssignmentDates = (assignment: AssignmentHistory) => {
    Alert.prompt(
      'Edit Assignment Dates',
      `Current Start Date: ${assignment.assignment_date ? new Date(assignment.assignment_date).toLocaleDateString() : 'Not set'}\nCurrent End Date: ${assignment.return_date ? new Date(assignment.return_date).toLocaleDateString() : 'Not set'}\n\nEnter new end date (YYYY-MM-DD) or leave empty:`,
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Update',
          onPress: async (endDate) => {
            try {
              if (endDate && endDate.trim()) {
                // TODO: Update assignment with new end date
                console.log('Updating assignment end date:', assignment.id, endDate);
                Alert.alert('Success', 'Assignment dates updated successfully');
                await fetchAssignments();
              } else {
                // Remove end date (set to null)
                console.log('Removing end date for assignment:', assignment.id);
                Alert.alert('Success', 'End date removed from assignment');
                await fetchAssignments();
              }
              setShowIssuesModal(false);
            } catch (error) {
              Alert.alert('Error', 'Failed to update assignment dates');
            }
          }
        }
      ]
    );
  };

  const handleDeleteAssignment = (assignment: AssignmentHistory) => {
    Alert.alert(
      'حذف التعيين | Delete Assignment',
      `هل أنت متأكد من حذف تعيين ${assignment.vehicle?.license_plate}؟\nAre you sure you want to delete assignment for ${assignment.vehicle?.license_plate}?\n\n⚠️ This action cannot be undone!`,
      [
        {
          text: 'إلغاء | Cancel',
          style: 'cancel'
        },
        {
          text: 'حذف | Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // Delete the assignment (activity logging handled automatically in API)
              await deleteAssignmentHistory(assignment.id);
              
              // Update vehicle status to available
              if (assignment.vehicle_id) {
                const { editVehicle } = useVehicleStore.getState();
                await editVehicle(assignment.vehicle_id, { carStatus: 'available' });
              }
              
              // Update driver statuses to active
              if (assignment.primary_driver_id) {
                const { editDriver } = useDriverStore.getState();
                await editDriver(assignment.primary_driver_id, { status: 'active' });
              }
              if (assignment.secondary_driver_id) {
                const { editDriver } = useDriverStore.getState();
                await editDriver(assignment.secondary_driver_id, { status: 'active' });
              }
              
              // Refresh assignments list
              await fetchAssignments();
              
              Alert.alert(
                'تم الحذف | Deleted',
                'تم حذف التعيين بنجاح | Assignment deleted successfully'
              );
              setShowIssuesModal(false);
            } catch (error) {
              console.error('Error deleting assignment:', error);
              Alert.alert(
                'خطأ | Error',
                'فشل حذف التعيين | Failed to delete assignment'
              );
            }
          }
        }
      ]
    );
  };

  const renderIssuesModal = () => (
    <Modal
      visible={showIssuesModal}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Data Issues & Fixes ({dataIssues.length})</Text>
          <TouchableOpacity onPress={() => setShowIssuesModal(false)}>
            <Ionicons name="close" size={24} color={theme.colors.gray600} />
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.issuesScrollView}>
          {dataIssues.length === 0 ? (
            <View style={styles.noIssuesContainer}>
              <Ionicons name="checkmark-circle" size={48} color="#10b981" />
              <Text style={styles.noIssuesText}>No Data Issues Found!</Text>
              <Text style={styles.noIssuesSubtext}>All assignments look good.</Text>
            </View>
          ) : (
            dataIssues.map((issue, index) => (
              <View key={issue.id} style={styles.issueItem}>
                <View style={styles.issueHeader}>
                  <Ionicons name="warning" size={20} color="#f59e0b" />
                  <View style={styles.issueHeaderText}>
                    <Text style={styles.issueTitle}>{issue.title}</Text>
                    <Text style={styles.issueNumber}>Issue #{index + 1} • {issue.severity.toUpperCase()} PRIORITY</Text>
                  </View>
                </View>
                
                <Text style={styles.issueDescription}>{issue.description}</Text>
                
                <View style={styles.assignmentDetailsContainer}>
                  <Text style={styles.assignmentDetailsTitle}>Assignment Details:</Text>
                  <Text style={styles.assignmentDetailsText}>
                    • ID: {issue.assignment.id}{'\n'}
                    • Vehicle: {issue.assignment.vehicle?.license_plate || 'Not found'}{'\n'}
                    • Make/Model: {issue.assignment.vehicle?.make_model || 'Unknown'}{'\n'}
                    • Driver: {issue.assignment.primaryDriver?.name || 'Not found'}{'\n'}
                    • Driver Phone: {issue.assignment.primaryDriver?.phone || 'Not found'}{'\n'}
                    • Status: {issue.assignment.status}{'\n'}
                    • Start Date: {issue.assignment.assignment_date ? new Date(issue.assignment.assignment_date).toLocaleDateString() : 'Not set'}{'\n'}
                    • End Date: {issue.assignment.return_date ? new Date(issue.assignment.return_date).toLocaleDateString() : 'Not set'}
                  </Text>
                </View>
                
                {/* Action Buttons */}
                <View style={styles.actionButtonsContainer}>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.editButton]}
                    onPress={() => navigation.navigate('ManageAssignment', { assignment: issue.assignment })}
                  >
                    <Ionicons name="create-outline" size={16} color="white" />
                    <Text style={styles.actionButtonText}>Edit Assignment</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={[styles.actionButton, styles.deleteButton]}
                    onPress={() => navigation.navigate('ManageAssignment', { assignment: issue.assignment })}
                  >
                    <Ionicons name="trash-outline" size={16} color="white" />
                    <Text style={styles.actionButtonText}>Delete Assignment</Text>
                  </TouchableOpacity>
                </View>
                
                {issue.actionable ? (
                  <View style={styles.suggestionContainer}>
                    <Text style={styles.suggestionTitle}>🔧 Automated Fix Available</Text>
                    <Text style={styles.suggestionDescription}>{issue.suggestedFix}</Text>
                  </View>
                ) : (
                  <View style={styles.manualFixContainer}>
                    <Text style={styles.manualFixTitle}>⚠️ Manual Fix Required</Text>
                    <Text style={styles.manualFixDescription}>{issue.suggestedFix}</Text>
                  </View>
                )}
              </View>
            ))
          )}
        </ScrollView>
      </View>
    </Modal>
  );

  // Show loading state if still loading and no data
  if (loading && assignments.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading assignment history...</Text>
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      {/* Statistics */}
      <View style={styles.statsContainer}>
        <View style={styles.statCard}>
          <Text style={styles.statNumber}>{stats.total}</Text>
          <Text style={styles.statLabel}>Total</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#f59e0b' }]}>{stats.active}</Text>
          <Text style={styles.statLabel}>Active</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#10b981' }]}>{stats.completed}</Text>
          <Text style={styles.statLabel}>Completed</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={[styles.statNumber, { color: '#ef4444' }]}>{stats.cancelled}</Text>
          <Text style={styles.statLabel}>Cancelled</Text>
        </View>
      </View>

      {/* Data Issues Alert */}
      {dataIssues.length > 0 && (
        <View style={styles.dataIssuesContainer}>
          <View style={styles.dataIssuesHeader}>
            <Ionicons name="warning" size={20} color="#f59e0b" />
            <Text style={styles.dataIssuesTitle}>
              Data Issues Detected ({dataIssues.length})
            </Text>
            <TouchableOpacity 
              style={styles.fixAllButton}
              onPress={() => setShowIssuesModal(true)}
            >
              <Text style={styles.fixAllButtonText}>Review & Fix</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {renderFilterButtons()}

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {/* Main Content - View Mode Dependent */}
      {/* Enhanced view modes */}
      {viewMode === 'timeline' ? (
        <ScrollView
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          showsVerticalScrollIndicator={false}
        >
          {filteredAssignments.length > 0 ? renderTimelineView() : (
            <View style={styles.emptyContainer}>
              <Ionicons name="git-branch-outline" size={64} color={theme.colors.gray400} />
              <Text style={styles.emptyText}>No timeline data available</Text>
              <Text style={styles.emptySubtext}>Assignment timeline will appear here</Text>
            </View>
          )}
        </ScrollView>
      ) : viewMode === 'cards' ? (
        <ScrollView
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.cardsContainer}
        >
          {filteredAssignments.length > 0 ? (
            filteredAssignments.map((assignment) => (
              <TouchableOpacity
                key={assignment.id}
                style={styles.assignmentCard}
                onPress={() => handleRowClick(assignment)}
              >
                <View style={styles.cardHeader}>
                  <Text style={styles.cardTitle}>
                    {assignment.vehicle?.license_plate}
                  </Text>
                  <View style={[styles.cardStatus, { backgroundColor: getStatusColor(assignment.status) }]}>
                    <Text style={styles.cardStatusText}>{getStatusDisplayText(assignment)}</Text>
                  </View>
                </View>
                <Text style={styles.cardSubtitle}>
                  {assignment.vehicle?.make_model} • {assignment.primaryDriver?.name}
                </Text>
                <View style={styles.cardDetails}>
                  <Text style={styles.cardDetail}>
                    Start: {new Date(assignment.assignment_date).toLocaleDateString()}
                  </Text>
                  <Text style={styles.cardDetail}>
                    Rate: AED {assignment.daily_rate}/day
                  </Text>
                  {assignment.return_date && (
                    <Text style={styles.cardDetail}>
                      End: {new Date(assignment.return_date).toLocaleDateString()}
                    </Text>
                  )}
                </View>
              </TouchableOpacity>
            ))
          ) : (
            <View style={styles.emptyContainer}>
              <Ionicons name="card-outline" size={64} color={theme.colors.gray400} />
              <Text style={styles.emptyText}>No assignment cards available</Text>
              <Text style={styles.emptySubtext}>Assignment cards will appear here</Text>
            </View>
          )}
        </ScrollView>
      ) : (
        <ScrollView
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.tableContainer}
        >
          {filteredAssignments.length > 0 ? (
        <ResponsiveTable
          data={filteredAssignments}
          columns={tableColumns}
          onRowPress={handleRowClick}
          loading={loading}
        />
          ) : (
            <View style={styles.emptyContainer}>
              <Ionicons name="clipboard-outline" size={64} color={theme.colors.gray400} />
              <Text style={styles.emptyText}>No assignment history found</Text>
              <Text style={styles.emptySubtext}>
                {statusFilter === 'all' 
                  ? 'Start by creating vehicle assignments'
                  : `No ${statusFilter} assignments found`
                }
              </Text>
            </View>
          )}
        </ScrollView>
      )}

      {/* Complete Assignment Modal */}
      <Modal
        visible={completeModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setCompleteModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Complete Assignment</Text>
            
            <Text style={styles.modalLabel}>Completed By:</Text>
            <TextInput
              style={styles.modalInput}
              value={completedBy}
              onChangeText={setCompletedBy}
              placeholder="Enter name of person completing"
              placeholderTextColor={theme.colors.gray400}
            />

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelModalButton]}
                onPress={() => {
                  setCompleteModalVisible(false);
                  setSelectedAssignmentId(null);
                  setCompletedBy('');
                }}
              >
                <Text style={styles.cancelModalButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalButton, styles.confirmModalButton]}
                onPress={handleCompleteAssignmentModal}
              >
                <Text style={styles.confirmModalButtonText}>Complete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Data Issues Modal */}
      {renderIssuesModal()}

      {/* Export Modal */}
      <Modal
        visible={exportModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setExportModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.exportModalContent}>
            <Text style={styles.modalTitle}>Export Assignment History</Text>
            <Text style={styles.exportSubtitle}>
              Export {filteredAssignments.length} assignments
            </Text>
            
            <View style={styles.exportOptions}>
              <TouchableOpacity
                style={styles.exportOption}
                onPress={() => handleExport('csv')}
              >
                <Ionicons name="document-text-outline" size={24} color={theme.colors.primary} />
                <Text style={styles.exportOptionText}>CSV File</Text>
                <Text style={styles.exportOptionSubtext}>Spreadsheet compatible</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.exportOption}
                onPress={() => handleExport('excel')}
              >
                <Ionicons name="grid-outline" size={24} color={theme.colors.primary} />
                <Text style={styles.exportOptionText}>Excel File</Text>
                <Text style={styles.exportOptionSubtext}>Advanced formatting</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.exportOption}
                onPress={() => handleExport('pdf')}
              >
                <Ionicons name="document-outline" size={24} color={theme.colors.primary} />
                <Text style={styles.exportOptionText}>PDF Report</Text>
                <Text style={styles.exportOptionSubtext}>Print-ready format</Text>
              </TouchableOpacity>
            </View>
            
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelModalButton]}
                onPress={() => setExportModalVisible(false)}
              >
                <Text style={styles.cancelModalButtonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.gray600,
  },
  statsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
  },
  statCard: {
    flex: 1,
    backgroundColor: theme.colors.white,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  statLabel: {
    fontSize: 12,
    color: theme.colors.gray600,
    marginTop: 4,
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 8,
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: theme.colors.white,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  activeFilterButton: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: 14,
    color: theme.colors.gray600,
    fontWeight: '500',
  },
  activeFilterButtonText: {
    color: theme.colors.white,
  },
  errorContainer: {
    margin: 16,
    padding: 16,
    backgroundColor: '#fee2e2',
    borderRadius: 8,
  },
  errorText: {
    color: '#dc2626',
    textAlign: 'center',
  },
  listContainer: {
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.gray200,
    marginVertical: 8,
  },
  listItem: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  listItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  listItemLeft: {
    flex: 1,
  },
  listItemRight: {
    alignItems: 'flex-end',
  },
  vehicleText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  driverText: {
    fontSize: 14,
    color: theme.colors.gray600,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  dateText: {
    fontSize: 12,
    color: theme.colors.gray500,
  },
  phoneText: {
    fontSize: 12,
    color: theme.colors.gray600,
  },
  rateText: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  photoIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  photoCountBadge: {
    fontSize: 11,
    fontWeight: '600',
    color: theme.colors.info,
  },
  listItemDetails: {
    marginBottom: 12,
  },
  detailText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  detailLabel: {
    fontWeight: '600',
    color: theme.colors.gray700,
  },
  listItemActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 6,
    gap: 6,
  },
  completeBtn: {
    backgroundColor: '#10b981',
  },
  cancelBtn: {
    backgroundColor: '#ef4444',
  },
  actionBtnText: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.gray600,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.gray500,
    marginTop: 8,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 20,
    textAlign: 'center',
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.gray700,
    marginBottom: 8,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 24,
    backgroundColor: theme.colors.white,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelModalButton: {
    backgroundColor: theme.colors.gray300,
  },
  confirmModalButton: {
    backgroundColor: theme.colors.primary,
  },
  cancelModalButtonText: {
    color: theme.colors.gray700,
    fontSize: 16,
    fontWeight: '600',
  },
  confirmModalButtonText: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  // Table Styles
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray300,
  },
  tableHeaderCell: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.gray700,
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
    alignItems: 'center',
  },
  tableCell: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  tableCellText: {
    fontSize: 13,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  statusBadgeCompact: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    minWidth: 50,
    alignItems: 'center',
  },
  statusTextCompact: {
    fontSize: 10,
    fontWeight: '600',
    color: theme.colors.white,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 4,
  },
  actionButtonCompact: {
    backgroundColor: theme.colors.primary,
    width: 32,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 2,
  },
  cancelButtonCompact: {
    backgroundColor: theme.colors.error,
  },
  actionButtonCompactText: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: 'bold',
  },
  // Forms Modal Styles
  fullScreenModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  fullScreenModalContent: {
    flex: 1,
    backgroundColor: theme.colors.white,
    marginTop: 50,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  modalHeaderTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    flex: 1,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  printButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: theme.colors.gray100,
    gap: 6,
  },
  printButtonText: {
    fontSize: 14,
    color: theme.colors.primary,
    fontWeight: '500',
  },
  closeButton: {
    padding: 4,
  },
  tabContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingTop: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  tabButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTabButton: {
    borderBottomColor: theme.colors.primary,
  },
  tabButtonText: {
    fontSize: 14,
    color: theme.colors.gray600,
    fontWeight: '500',
  },
  activeTabButtonText: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  formContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  assignmentFormDetails: {
    paddingVertical: 20,
  },
  formContainer: {
    paddingVertical: 20,
  },
  formSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  // Data Issues UI Styles
  dataIssuesContainer: {
    margin: 16,
    backgroundColor: '#fef3c7',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#f59e0b',
  },
  dataIssuesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
  },
  dataIssuesTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#92400e',
  },
  fixAllButton: {
    backgroundColor: '#f59e0b',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  fixAllButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  // Modal Styles
  modalContainer: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },

  issuesScrollView: {
    flex: 1,
    padding: 16,
  },
  issueItem: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  issueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  issueNumber: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  issueDescription: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 12,
    lineHeight: 20,
  },
  suggestionContainer: {
    backgroundColor: '#f0f9ff',
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#3b82f6',
  },
  suggestionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: 4,
  },
  suggestionDescription: {
    fontSize: 12,
    color: '#1e3a8a',
    marginBottom: 8,
    lineHeight: 16,
  },
  fixButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3b82f6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    alignSelf: 'flex-start',
    gap: 6,
  },
  fixButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  noSuggestionContainer: {
    backgroundColor: '#fef2f2',
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#ef4444',
  },
  noSuggestionText: {
    fontSize: 12,
    color: '#991b1b',
    fontStyle: 'italic',
  },
  noIssuesContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
  },
  noIssuesText: {
    fontSize: 16,
    color: '#10b981',
    fontWeight: '600',
    marginTop: 12,
  },
  noIssuesSubtext: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 8,
    textAlign: 'center',
  },
  highSeverityIssue: {
    borderLeftWidth: 4,
    borderLeftColor: '#ef4444',
  },
  mediumSeverityIssue: {
    borderLeftWidth: 4,
    borderLeftColor: '#f59e0b',
  },
  issueHeaderText: {
    flex: 1,
    marginLeft: 12,
  },
  issueTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  assignmentDetailsContainer: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  assignmentDetailsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.primary,
    marginBottom: 8,
  },
  assignmentDetailsText: {
    fontSize: 13,
    color: theme.colors.gray700,
    lineHeight: 18,
    fontFamily: 'monospace',
  },
  manualFixContainer: {
    backgroundColor: '#fef3c7',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#f59e0b',
  },
  manualFixTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#92400e',
    marginBottom: 4,
  },
  manualFixDescription: {
    fontSize: 13,
    color: '#92400e',
    lineHeight: 16,
  },
  detailValue: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    flex: 1,
  },
  formContainerAlt: {
    paddingVertical: 20,
  },
  formDataContainer: {
    backgroundColor: theme.colors.gray50,
    borderRadius: 8,
    padding: 16,
    marginTop: 12,
  },
  formNote: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 16,
    fontStyle: 'italic',
  },
  placeholderText: {
    fontSize: 14,
    color: theme.colors.gray500,
    textAlign: 'center',
    paddingVertical: 40,
  },
  printModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },
  printModalContent: {
    flex: 1,
    backgroundColor: theme.colors.white,
    margin: 20,
    borderRadius: 12,
  },
  printModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  printModalTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    flex: 1,
  },
  printModalButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  printModalButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: theme.colors.primary,
    gap: 6,
  },
  closeModalButton: {
    backgroundColor: theme.colors.gray200,
  },
  printModalButtonText: {
    fontSize: 14,
    color: 'white',
    fontWeight: '600',
  },
  closeModalButtonText: {
    color: theme.colors.gray700,
  },
  printPreview: {
    flex: 1,
    padding: 20,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.white,
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 6,
    gap: 6,
  },
  completeButton: {
    backgroundColor: '#10b981',
  },
  tableContainer: {
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  editButton: {
    flex: 1,
    backgroundColor: theme.colors.blue600,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  deleteButton: {
    flex: 1,
    backgroundColor: '#ef4444',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  actionButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  // Enhanced Assignment History Styles
  enhancedHeader: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  metricsContainer: {
    marginBottom: 16,
  },
  metricsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  metricsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metricItem: {
    alignItems: 'center',
    flex: 1,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  metricLabel: {
    fontSize: 12,
    color: theme.colors.gray600,
    marginTop: 4,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.gray100,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 16,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  viewModeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewModeToggle: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    borderRadius: 8,
    padding: 2,
  },
  viewModeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 4,
  },
  activeViewModeButton: {
    backgroundColor: theme.colors.primary,
  },
  viewModeText: {
    fontSize: 12,
    color: theme.colors.gray600,
    fontWeight: '500',
  },
  activeViewModeText: {
    color: theme.colors.white,
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: theme.colors.gray100,
    borderRadius: 6,
    gap: 4,
  },
  exportButtonText: {
    fontSize: 12,
    color: theme.colors.primary,
    fontWeight: '500',
  },
  // Timeline View Styles
  timelineContainer: {
    padding: 16,
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: theme.colors.primary,
    marginTop: 4,
    marginRight: 12,
  },
  timelineContent: {
    flex: 1,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  timelineDate: {
    fontSize: 12,
    color: theme.colors.gray500,
    marginBottom: 4,
  },
  timelineTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  timelineSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 8,
  },
  timelineStatus: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  timelineStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: theme.colors.white,
  },
  // Cards View Styles
  cardsContainer: {
    padding: 16,
  },
  assignmentCard: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  cardStatus: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  cardStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: theme.colors.white,
  },
  cardSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 12,
  },
  cardDetails: {
    gap: 4,
  },
  cardDetail: {
    fontSize: 12,
    color: theme.colors.gray500,
  },
  // Export Modal Styles
  exportModalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  exportSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 20,
    textAlign: 'center',
  },
  exportOptions: {
    gap: 12,
    marginBottom: 24,
  },
  exportOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: theme.colors.gray50,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    gap: 12,
  },
  exportOptionText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    flex: 1,
  },
  exportOptionSubtext: {
    fontSize: 12,
    color: theme.colors.gray500,
    marginTop: 2,
  },
  // Missing styles
  activeViewMode: {
    backgroundColor: theme.colors.primary,
  },
  cancelButton: {
    backgroundColor: theme.colors.gray200,
  },
  confirmButton: {
    backgroundColor: theme.colors.primary,
  },
  cancelButtonText: {
    color: theme.colors.gray600,
    fontWeight: '600',
  },
  confirmButtonText: {
    color: theme.colors.white,
    fontWeight: '600',
  },
  modalSubtitle: {
    fontSize: 16,
    color: theme.colors.gray600,
    marginBottom: 16,
  },
  // Web Modal Styles
  webModalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center',
  },
  webModalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 20,
    width: '90%',
    maxWidth: 600,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 5,
  },
  // Shared Assignment Styles
  primaryDriverIndicator: {
    fontSize: 10,
    color: theme.colors.primary,
    fontWeight: '600',
  },
  secondaryDriverText: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
    marginTop: 2,
  },
  partnerIndicator: {
    color: theme.colors.blue600,
    fontWeight: '500',
  },
  // Hybrid view styles
  hybridContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
});
